import { useEffect, useLayoutEffect, useRef, useState, type ReactNode, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { CheckIcon } from './icons';
import { growClose, growOpen, GROW_CLOSE_MS } from '../motion/grow';

/** Keep an element mounted long enough to play its exit transition. */
export function usePresence(open: boolean, exitMs = 140): { mounted: boolean; closing: boolean } {
  const [mounted, setMounted] = useState(open);
  const [prevOpen, setPrevOpen] = useState(open);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) setMounted(true);
  }
  useEffect(() => {
    if (open) return;
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    const t = setTimeout(() => setMounted(false), reduce ? 0 : exitMs);
    return () => clearTimeout(t);
  }, [open, exitMs]);
  return { mounted: mounted || open, closing: !open };
}

export type Placement = 'below-start' | 'below-end' | 'above' | 'above-start';

interface PopoverProps {
  open: boolean;
  anchor: HTMLElement | null;
  /** Open at a screen point instead of beside the anchor (context menus). */
  point?: { x: number; y: number } | null;
  placement?: Placement;
  onClose: (reason: 'escape' | 'outside' | 'select') => void;
  label: string;
  className?: string;
  role?: 'menu' | 'dialog';
  /**
   * Grow out of the anchor (its `[data-morph-source]`, if it has one) and fold
   * back into it, instead of the usual small pop.
   */
  grow?: boolean;
  children: ReactNode;
}

const MARGIN = 10;
const GAP = 8;

/**
 * An anchored floating surface. Focus moves into it on open and returns to
 * the anchor on Escape; outside presses close it without stealing the press.
 */
export function Popover({ open, anchor, point = null, placement = 'below-start', onClose, label, className = '', role = 'dialog', grow = false, children }: PopoverProps) {
  const { mounted, closing } = usePresence(open, grow ? GROW_CLOSE_MS : 140);
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ x: number; y: number; origin: string } | null>(null);
  const grown = useRef(false);
  const onCloseRef = useRef(onClose);
  useLayoutEffect(() => {
    onCloseRef.current = onClose;
  });

  useLayoutEffect(() => {
    if (!mounted || (!anchor && !point) || closing) return;
    const place = () => {
      const el = ref.current;
      if (!el) return;
      const a = point ? new DOMRect(point.x, point.y, 0, 0) : anchor!.getBoundingClientRect();
      const { width, height } = el.getBoundingClientRect();
      let x = placement === 'below-end' ? a.right - width : placement === 'above' ? a.left + a.width / 2 - width / 2 : a.left;
      x = Math.min(window.innerWidth - width - MARGIN, Math.max(MARGIN, x));
      const above = placement.startsWith('above');
      const gap = point ? 2 : GAP;
      let y = above ? a.top - height - gap : a.bottom + gap;
      if (!above && y + height > window.innerHeight - MARGIN) y = Math.max(MARGIN, a.top - height - gap);
      if (above && y < MARGIN) y = a.bottom + GAP;
      const originX = Math.min(width, Math.max(0, a.left + a.width / 2 - x));
      setPos({ x, y, origin: `${originX}px ${y < a.top ? '100%' : '0%'}` });
      el.dataset.side = y < a.top ? 'above' : 'below';
    };
    place();
    const observer = new ResizeObserver(place);
    if (ref.current) observer.observe(ref.current);
    window.addEventListener('resize', place);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', place);
    };
  }, [mounted, anchor, point, placement, closing]);

  // Grow once placed (never from a stale place), fold back on closing; either may interrupt the other.
  useLayoutEffect(() => {
    const el = ref.current;
    const body = el?.firstElementChild as HTMLElement | null;
    if (!grow || !pos || !el || !body) return;
    const source = anchor?.querySelector('[data-morph-source]') ?? anchor;
    if (!closing && !grown.current) {
      grown.current = true;
      growOpen(el, body, source);
    } else if (closing && grown.current) {
      grown.current = false;
      growClose(el, body, source);
    }
  }, [grow, pos, closing, anchor]);

  // Focus moves in once the surface is placed (a hidden one cannot take it).
  const placed = !!pos;
  useEffect(() => {
    if (!open || !placed) return;
    const el = ref.current;
    const first = el?.querySelector<HTMLElement>('[data-autofocus]') ?? el?.querySelector<HTMLElement>('[role^="menuitem"]:not([disabled]), button:not([disabled]), input');
    first?.focus({ preventScroll: true });
  }, [open, placed]);

  useEffect(() => {
    if (!open) return;
    const el = ref.current;
    const down = (e: PointerEvent) => {
      const target = e.target as Node;
      if (el?.contains(target) || anchor?.contains(target)) return;
      onCloseRef.current('outside');
    };
    document.addEventListener('pointerdown', down, true);
    return () => document.removeEventListener('pointerdown', down, true);
  }, [open, anchor]);

  if (!mounted) {
    // The next opening is placed afresh before anything is drawn.
    if (pos) setPos(null);
    return null;
  }

  const keyDown = (e: ReactKeyboardEvent) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onClose('escape');
      anchor?.focus({ preventScroll: true });
    } else if (e.key === 'Tab' && role === 'menu') {
      e.preventDefault();
    }
  };

  return createPortal(
    <div
      ref={ref}
      className={`popover ${className}`}
      data-state={closing ? 'closing' : 'open'}
      data-grow={grow || undefined}
      // Folding away, it can no longer be used, even before it is out of sight.
      inert={(grow && closing) || undefined}
      role={role}
      aria-label={label}
      onKeyDown={keyDown}
      style={pos ? { transform: `translate(${Math.round(pos.x)}px, ${Math.round(pos.y)}px)`, transformOrigin: pos.origin } : { visibility: 'hidden' }}
    >
      <div className="popover-body" style={pos ? { transformOrigin: pos.origin } : undefined}>
        {children}
      </div>
    </div>,
    document.body,
  );
}

/** Arrow-key navigation for a menu's items. */
export function menuKeys(e: ReactKeyboardEvent<HTMLElement>) {
  const items = Array.from(e.currentTarget.querySelectorAll<HTMLElement>('[role^="menuitem"]:not([disabled])'));
  const index = items.indexOf(document.activeElement as HTMLElement);
  let next = -1;
  if (e.key === 'ArrowDown') next = (index + 1) % items.length;
  else if (e.key === 'ArrowUp') next = (index - 1 + items.length) % items.length;
  else if (e.key === 'Home') next = 0;
  else if (e.key === 'End') next = items.length - 1;
  if (next >= 0) {
    e.preventDefault();
    items[next]?.focus();
  }
}

interface MenuItemProps {
  icon?: ReactNode;
  label: ReactNode;
  hint?: string;
  checked?: boolean;
  /** An on/off switch rather than one choice among several. */
  toggle?: boolean;
  disabled?: boolean;
  className?: string;
  onSelect: () => void;
}

export function MenuItem({ icon, label, hint, checked, toggle, disabled, className = '', onSelect }: MenuItemProps) {
  return (
    <button
      type="button"
      role={checked === undefined ? 'menuitem' : toggle ? 'menuitemcheckbox' : 'menuitemradio'}
      aria-checked={checked}
      className={`menu-item ${className}`}
      data-toggle={toggle || undefined}
      disabled={disabled}
      tabIndex={-1}
      onClick={onSelect}
    >
      {icon !== undefined && <span className="menu-icon">{icon}</span>}
      <span className="menu-label">{label}</span>
      {hint && <kbd className="menu-hint">{hint}</kbd>}
      {toggle ? (
        <span className="menu-switch" data-on={checked || undefined} aria-hidden="true">
          <span className="menu-switch-knob" />
        </span>
      ) : (
        checked !== undefined && <span className="menu-check">{checked && <CheckIcon size={15} />}</span>
      )}
    </button>
  );
}
